import type { ListerMode, ListerStatus, ListerStep } from "../../../lib/api";

// Defined once and reused by Review.tsx's own STAGE_LABEL and its item-session waiting copy
// (Josh's constants rule; send-to-lister Phase 3 review M3), instead of each typing "Mapping to
// Shopify" again.
export const MAP_STAGE_LABEL = "Mapping to Shopify";

const LABELS: Record<string, string> = {
  upcitemdb: "UPC database", goupc: "Go-UPC", serp_google: "Google", serp_shopping: "Google Shopping", serp_ebay_active: "eBay active", ebay_raw_comps: "eBay ungraded comps", serp_ebay_sold: "eBay sold",
  ebay_browse: "eBay by GTIN", ebay_item: "eBay item details", pricecharting: "PriceCharting", market_data: "eBay market data", images: "Images",
  identify: "Identifying", refute: "Cross-checking", map: MAP_STAGE_LABEL, pipeline: "Pipeline",
};
const PIPELINE = ["images", "identify", "refute", "map"];
// An item session (send-to-lister task L6) is never researched, identified or cross-checked:
// Buy / Trade's photographs, then the Map pass, are its only stages.
const ITEM_PIPELINE = ["images", "map"];
const ORDER = ["upcitemdb", "goupc", "serp_google", "serp_shopping", "serp_ebay_active", "ebay_raw_comps", "ebay_browse", "pricecharting", "market_data", "images", "identify", "refute", "map"]; // serp_ebay_sold is disabled in deps.ts (SerpAPI sold endpoint hangs); still labelled if an old session logged it

export default function StepProgress({ steps, status, mode }: { steps: ListerStep[]; status: ListerStatus; mode?: ListerMode }) {
  const byStep = new Map<string, ListerStep>();
  for (const s of steps) byStep.set(s.step, s);   // last wins
  const running = ["researching", "proposing", "posting"].includes(status);
  const pipeline = mode === "item" ? ITEM_PIPELINE : PIPELINE;
  const isSource = (k: string) => !PIPELINE.includes(k);
  // Research sources are optional/configurable (Go-UPC needs a key, eBay-sold is disabled), so only the ones
  // that actually reported are listed — in ORDER first, then anything unknown — plus one "in progress" row until
  // the images step lands. The pipeline stages are always shown while running so the operator sees what is next.
  const sourceKeys = [...ORDER.filter((k) => isSource(k) && byStep.has(k)), ...[...byStep.keys()].filter((k) => isSource(k) && !ORDER.includes(k))];
  const researching = mode !== "item" && running && !byStep.has("images") && status === "researching";
  const rows: Array<{ key: string; label: string; s?: ListerStep }> = [
    ...sourceKeys.map((k) => ({ key: k, label: LABELS[k] ?? k, s: byStep.get(k) })),
    ...(researching ? [{ key: "__sources", label: `Research sources… (${sourceKeys.length} done)` }] : []),
    ...pipeline.filter((k) => byStep.has(k) || running).map((k) => ({ key: k, label: LABELS[k] ?? k, s: byStep.get(k) })),
  ];
  return (
    <ul className="space-y-1 text-xs font-mono">
      {rows.map(({ key, label, s }) => (
        <li key={key} className="flex items-baseline gap-2">
          <span className={s ? (s.ok ? "text-green-300" : "text-red-300") : "text-zinc-600"}>{s ? (s.ok ? "✓" : "✗") : "…"}</span>
          <span className={s ? "text-zinc-300" : "text-zinc-600"}>{label}</span>
          {s?.note && <span className="text-zinc-500 truncate">{s.note}</span>}
          {s && <span className="ml-auto text-zinc-600">{(s.ms / 1000).toFixed(1)}s</span>}
        </li>
      ))}
    </ul>
  );
}
